import React, { useMemo, useState } from 'react'
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore'
import { getBlob, ref } from 'firebase/storage'
import { db, storage } from '../../../firebase'
import Button from '../../../components/ui/Button'
import { buildRissExport, RISS_HEADERS, RISS_WEIGHT_UNITS } from '../lib/rissExport.mjs'
import { prepareRissAttachments, createRissZip } from '../lib/rissAttachments.mjs'

const TIME_ZONE = 'America/New_York'
const DATE_PARTS = new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
const inputClass = 'w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground'
const OPTIONAL_FIELDS = ['Customer DOB', 'Customer Street', 'Customer City', 'Customer State', 'Customer Zip', 'Manufacturer', 'Model', 'Serial #', 'Inscription', 'Item Filename', 'Customer ID Filename', 'Customer Filename']

function today() {
  const parts = Object.fromEntries(DATE_PARTS.formatToParts(new Date()).map(p => [p.type, p.value]))
  return `${parts.year}-${parts.month}-${parts.day}`
}

function isDay(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const date = new Date(`${value}T00:00:00Z`)
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value
}

export async function loadRissPurchases({ startDate, endDate }) {
  if (!isDay(startDate) || !isDay(endDate) || startDate > endDate) throw new Error('Choose a valid date range.')
  // Bound the cloud read conservatively across both DST offsets. The pure
  // exporter then applies the precise, inclusive local-calendar date filter.
  const lower = new Date(`${startDate}T00:00:00Z`)
  lower.setUTCDate(lower.getUTCDate() - 1)
  const upper = new Date(`${endDate}T00:00:00Z`)
  upper.setUTCDate(upper.getUTCDate() + 2)
  const result = await getDocs(query(collection(db, 'gold_transactions'), where('createdAt', '>=', lower), where('createdAt', '<', upper), orderBy('createdAt', 'asc'), limit(5001)))
  if (result.size > 5000) throw new Error('More than 5,000 purchases match this range. Choose a smaller range; nothing was truncated or exported.')
  return result.docs.map(d => ({ ...d.data(), id: d.id }))
}

export async function downloadRissPhoto(source) {
  if (typeof source !== 'string') throw new Error('Invalid photo reference.')
  if (/^data:image\/(png|jpeg);base64,/i.test(source)) return (await fetch(source)).blob()
  const photo = ref(storage, source)
  if (photo.bucket !== ref(storage).bucket) throw new Error('A photo points outside this app’s storage bucket. Review it before exporting.')
  return getBlob(photo, 15 * 1024 * 1024 + 1)
}

function downloadFile(bytes, filename, type) {
  const url = URL.createObjectURL(new Blob([bytes], { type }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/** loadTransactions/downloadPhoto are injectable for fully offline UI tests. */
export default function RissExportPanel({ loadTransactions = loadRissPurchases, downloadPhoto = downloadRissPhoto }) {
  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(today)
  const [weightUnit, setWeightUnit] = useState('')
  const [requiredFields, setRequiredFields] = useState([])
  const [includePhotos, setIncludePhotos] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const [transactions, setTransactions] = useState(null)
  const [excluded, setExcluded] = useState(new Set())
  const [attachments, setAttachments] = useState(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const selected = useMemo(() => (transactions || []).filter(t => !excluded.has(t.id)), [transactions, excluded])
  const options = useMemo(() => ({ startDate, endDate, weightUnit, timeZone: TIME_ZONE, requiredFields, attachmentFilenames: includePhotos ? attachments?.attachmentFilenames : undefined }), [startDate, endDate, weightUnit, requiredFields, includePhotos, attachments])
  const result = useMemo(() => transactions ? buildRissExport(selected, options) : null, [transactions, selected, options])
  // A required filename cannot exist until its photo has been prepared. Do
  // not create a validation deadlock by requiring it in this preparatory pass.
  const photoEligibleIds = useMemo(() => includePhotos && transactions
    ? buildRissExport(selected, { ...options, requiredFields: requiredFields.filter(f => !f.endsWith('Filename')), attachmentFilenames: undefined }).eligibleTransactionIds
    : [], [includePhotos, transactions, selected, options, requiredFields])
  const canDownload = acknowledged && result?.rowCount > 0 && !result?.errors.length && (!includePhotos || attachments) && !busy

  const resetReview = () => { setAcknowledged(false); setError(''); setNotice('') }
  const changeRange = (setter, value) => {
    setter(value)
    setTransactions(null)
    setExcluded(new Set())
    setAttachments(null)
    resetReview()
  }
  const toggleRequired = field => {
    setRequiredFields(prev => prev.includes(field) ? prev.filter(v => v !== field) : [...prev, field])
    // Changing validation may make additional purchases eligible. Their
    // photos were not necessarily included in the previous preparation.
    setAttachments(null)
    resetReview()
  }
  const toggleTransaction = id => {
    setExcluded(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next })
    setAttachments(null)
    resetReview()
  }
  const load = async () => {
    setBusy('Reading saved purchases…')
    setError(''); setNotice(''); setAcknowledged(false); setAttachments(null); setTransactions(null); setExcluded(new Set())
    try {
      if (!isDay(startDate) || !isDay(endDate) || startDate > endDate || !weightUnit) throw new Error('Choose a valid date range and confirm the output weight unit first.')
      setTransactions(await loadTransactions({ startDate, endDate }))
    } catch (e) { setError(e.message || 'Could not read purchases.') }
    finally { setBusy('') }
  }
  const preparePhotos = async () => {
    setBusy('Preparing private photos for this download…')
    setError(''); setAcknowledged(false); setAttachments(null)
    try {
      const included = new Set(photoEligibleIds)
      if (!included.size) throw new Error('No eligible purchases. Review validation issues before preparing photos.')
      setAttachments(await prepareRissAttachments(selected.filter(t => included.has(t.id)), downloadPhoto))
    } catch (e) { setError(e.message || 'Could not prepare photos. No files were downloaded.') }
    finally { setBusy('') }
  }
  const download = batch => {
    if (!canDownload) return
    try {
      if (includePhotos) {
        const batchIndex = result.batches.indexOf(batch)
        const firstRow = result.batches.slice(0, batchIndex).reduce((sum, b) => sum + b.rowCount, 0)
        const filenames = new Set(result.rows.slice(firstRow, firstRow + batch.rowCount).flatMap(row => row.slice(22, 25)).filter(Boolean))
        const images = attachments.files.filter(f => filenames.has(f.name))
        const zip = createRissZip([{ name: batch.filename, bytes: batch.csv }, ...images])
        downloadFile(zip, batch.filename.replace(/\.csv$/i, '.zip'), 'application/zip')
      } else downloadFile(batch.csv, batch.filename, 'text/csv;charset=utf-8')
      setNotice('Downloaded locally only. Nothing was sent to RISS or marked as reported. Upload each batch separately and verify its acceptance in RISS.')
    } catch (e) { setError(e.message || 'Could not create the download.') }
  }
  const issues = [...(result?.errors || []), ...(result?.warnings || []), ...(attachments?.warnings || [])]

  return (
    <section aria-labelledby="riss-heading" className="rounded-2xl border border-border bg-card p-5 space-y-5">
      <div>
        <h2 id="riss-heading" className="text-lg font-semibold text-foreground">RISS Police Export</h2>
        <p className="text-sm text-muted-foreground mt-1">Create the official 25-column CSV, one row per item. This does not send a police report or change any purchase.</p>
      </div>
      <div className="rounded-xl border border-warning/30 bg-warning/5 p-3 text-sm space-y-1">
        <p>Before live use, confirm your shop’s <a className="underline" href="https://rissprop-uploader.riss.net/help" target="_blank" rel="noopener noreferrer">My Required Fields</a> and the weight unit with RISS. Template columns alone do not establish local reporting requirements.</p>
        <p>Reads saved Firebase purchases. Local browser-only test purchases are not included. Historical purchases with unknown amounts or cancelled/deleted status require review.</p>
      </div>
      <fieldset disabled={!!busy} className="space-y-4 disabled:opacity-60">
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-sm">From date<input aria-label="RISS from date" type="date" value={startDate} onChange={e => changeRange(setStartDate, e.target.value)} className={inputClass} /></label>
          <label className="text-sm">Through date<input aria-label="RISS through date" type="date" value={endDate} onChange={e => changeRange(setEndDate, e.target.value)} className={inputClass} /></label>
          <label className="text-sm">Weight unit (confirm with RISS)<select aria-label="RISS weight unit" value={weightUnit} onChange={e => { setWeightUnit(e.target.value); setAttachments(null); resetReview() }} className={inputClass}>
            <option value="">Select confirmed unit</option>
            {RISS_WEIGHT_UNITS.map(unit => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
          </select></label>
        </div>
        <p className="text-xs text-muted-foreground">Dates and times: {TIME_ZONE}, including daylight-saving time. Both selected dates are included.</p>
        <details className="rounded-lg border border-border p-3">
          <summary className="cursor-pointer text-sm font-medium">Additional fields required by your shop’s RISS configuration ({requiredFields.length})</summary>
          <p className="text-xs text-muted-foreground mt-2">Select only requirements you have confirmed. Missing selected fields block download; other missing details are shown as review warnings.</p>
          <div className="grid gap-2 mt-3 sm:grid-cols-2 lg:grid-cols-3">
            {OPTIONAL_FIELDS.map(field => <label key={field} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={requiredFields.includes(field)} onChange={() => toggleRequired(field)} />{field}</label>)}
          </div>
        </details>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={includePhotos} onChange={e => { setIncludePhotos(e.target.checked); setAttachments(null); resetReview() }} className="mt-1" />Include available item, ID and customer photos in a ZIP (private personal information; excludes signatures)</label>
        <Button type="button" onClick={load} disabled={!weightUnit || !startDate || !endDate} iconName="Search" iconPosition="left">Review purchases</Button>
      </fieldset>

      {busy && <p role="status" className="text-sm text-primary">{busy}</p>}
      {error && <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
      {transactions && <>
        <div className="flex flex-wrap gap-3 text-sm">
          <span>{result.transactionCount} eligible purchases · {result.rowCount} item rows</span>
          <span className="text-destructive">{result.errors.length} blocking issues</span>
          <span className="text-muted-foreground">{excluded.size} purchases manually excluded; none deleted or marked reported</span>
        </div>
        <details className="rounded-lg border border-border p-3">
          <summary className="text-sm cursor-pointer">Select purchases / explicitly exclude a record for review</summary>
          <p className="text-xs text-muted-foreground my-2">The reader includes a small date-boundary buffer. Only purchases inside your exact local date range can be exported. Excluding a record does not resolve a reporting obligation.</p>
          <div className="max-h-64 overflow-auto space-y-2">
            {transactions.map(tx => <label key={tx.id} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!excluded.has(tx.id)} disabled={!!busy} onChange={() => toggleTransaction(tx.id)} />{tx.transactionNumber || tx.id} · {tx.status || 'completed'}</label>)}
          </div>
        </details>
        {issues.length > 0 && <details open={result.errors.length > 0} className="rounded-lg border border-border p-3">
          <summary className="text-sm cursor-pointer">Validation and review notes ({issues.length})</summary>
          <ul className="mt-2 space-y-1 max-h-64 overflow-auto text-sm text-muted-foreground">
            {issues.slice(0, 80).map((issue, i) => <li key={i}>{issue.transactionId ? `${issue.transactionId}: ` : ''}{issue.message}</li>)}
          </ul>
          {issues.length > 80 && <p className="text-xs mt-2">Showing the first 80 notes. All {issues.length} notes still participate in validation.</p>}
        </details>}
        {includePhotos && <Button type="button" variant="outline" onClick={preparePhotos} disabled={!!busy || !photoEligibleIds.length}>Prepare selected photos</Button>}
        {attachments && <p className="text-xs text-muted-foreground">{attachments.files.length} photos prepared in memory. No photo URLs or credentials are included in CSV files.</p>}
        {result.rows.length > 0 && <details className="rounded-lg border border-border p-3">
          <summary className="text-sm cursor-pointer">Preview first {Math.min(10, result.rows.length)} item rows — contains private customer information</summary>
          <div className="overflow-auto max-h-72 mt-3">
            <table className="text-xs whitespace-nowrap"><thead><tr>{RISS_HEADERS.map(h => <th key={h} className="text-left px-2 py-1 border-b border-border">{h}</th>)}</tr></thead>
              <tbody>{result.rows.slice(0, 10).map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j} className="px-2 py-1 border-b border-border/40">{cell}</td>)}</tr>)}</tbody>
            </table>
          </div>
        </details>}
        <label className="flex items-start gap-2 text-sm"><input aria-label="Confirm RISS requirements reviewed" type="checkbox" checked={acknowledged} disabled={!!busy} onChange={e => setAcknowledged(e.target.checked)} className="mt-1" />I have reviewed the data, exclusions, my shop’s required fields and the output weight unit. I understand that downloading does not submit or confirm acceptance of a report.</label>
        <p className="text-xs text-muted-foreground">Maximum 1,000 item rows per batch. Upload each CSV/ZIP separately; do not combine batches in one RISS upload. Keep downloaded files private.</p>
        <div className="flex flex-wrap gap-2">
          {result.batches.map((batch, index) => <Button key={batch.filename} type="button" disabled={!canDownload} onClick={() => download(batch)} iconName="Download" iconPosition="left">Download {includePhotos ? 'ZIP' : 'CSV'} {index + 1}/{result.batches.length} ({batch.rowCount} items)</Button>)}
        </div>
        {!result.rowCount && !result.errors.length && <p className="text-sm text-muted-foreground">No eligible item rows in this date range.</p>}
      </>}
      {notice && <p role="status" className="rounded-lg bg-success/10 p-3 text-sm">{notice}</p>}
    </section>
  )
}
